import dayjs from "dayjs";
import { ApiService } from "../config";
import { whatsappApi, type WhatsappNaFicha } from "../modules/profiles-tracking/services/whatsapp";
import {
  formatarParaCampo,
  normalizarNumero,
} from "../modules/profiles-tracking/components/edit-profile-modal/components/whatsapp-no-google";

export { normalizarNumero };
export type { WhatsappNaFicha };

export type ResultadoDaConfirmacao = "CONFIRMOU" | "CORRIGIU";

export type ConfirmacaoDoWhatsapp = {
  resultado: ResultadoDaConfirmacao;
  numeroConfirmado: string | null;
  numeroNoGoogle: string | null;
  confirmadoEm: string | null;
  usuario: string | null;
};

export const numeroDoLink = (link?: string | null): string | null =>
  (link ?? "").match(/wa\.me\/\+?(\d+)/i)?.[1] ?? null;

export const numeroAtualNoGoogle = (whatsapp?: WhatsappNaFicha | null): string | null =>
  normalizarNumero(whatsapp?.numeroNoGoogle) ?? normalizarNumero(numeroDoLink(whatsapp?.linkNoGoogle));

/**
 * O cartão "Ele chama você?" aparece quando o Google mostra um botão de
 * WhatsApp na ficha e ninguém confirmou esse número. Se o número no Google
 * mudar depois da confirmação, ele volta.
 */
export const deveMostrarAviso = (
  whatsapp?: WhatsappNaFicha | null,
  confirmacao?: ConfirmacaoDoWhatsapp | null,
): boolean => {
  if (!whatsapp?.linkNoGoogle) return false;
  const atual = numeroAtualNoGoogle(whatsapp);
  if (!atual) return false;
  return normalizarNumero(confirmacao?.numeroConfirmado) !== atual;
};

/** 5562996640758 → (62) 99664-0758 */
export const formatarNumero = (numero?: string | null): string => formatarParaCampo(numero);

/** Equipe: o que o cliente respondeu no cartão "Ele chama você?". */
export const textoDaConfirmacao = (confirmacao: ConfirmacaoDoWhatsapp): string => {
  const data = confirmacao.confirmadoEm ? dayjs(confirmacao.confirmadoEm).format("DD/MM") : "";
  if (confirmacao.resultado === "CORRIGIU") {
    return `Cliente corrigiu para ${formatarNumero(confirmacao.numeroConfirmado)}${data ? ` em ${data}` : ""}.`;
  }
  return `Cliente confirmou o WhatsApp${data ? ` em ${data}` : ""}: ${formatarNumero(confirmacao.numeroConfirmado)}.`;
};

const desembrulhar = <T>(resposta: unknown): T | null => {
  const corpo = (resposta as { data?: unknown } | undefined)?.data;
  if (corpo && typeof corpo === "object" && "data" in corpo) {
    return ((corpo as { data?: T | null }).data ?? null) as T | null;
  }
  return (corpo as T | undefined) ?? null;
};

const rota = (publicId: string) => `/business-profiles/${publicId}/whatsapp`;

/** Sem leitura (erro, sem acesso, Google fora) o cartão fica escondido. */
export const buscarWhatsappDaFicha = async (publicId: string): Promise<WhatsappNaFicha | null> => {
  try {
    return await whatsappApi.consultar(publicId);
  } catch {
    return null;
  }
};

export const buscarConfirmacao = async (publicId: string): Promise<ConfirmacaoDoWhatsapp | null> => {
  try {
    return desembrulhar<ConfirmacaoDoWhatsapp>(await ApiService.get(`${rota(publicId)}/confirmacao`));
  } catch {
    return null;
  }
};

export const corrigirWhatsapp = (publicId: string, numero: string): Promise<WhatsappNaFicha> =>
  whatsappApi.salvar(publicId, numero);

export const registrarConfirmacao = async (
  publicId: string,
  pedido: { resultado: ResultadoDaConfirmacao; numero: string; numeroNoGoogle: string | null },
): Promise<ConfirmacaoDoWhatsapp | null> =>
  desembrulhar<ConfirmacaoDoWhatsapp>(await ApiService.post(`${rota(publicId)}/confirmacao`, pedido));
